import { /*useMemo,*/ useLayoutEffect } from "react";
// import { ResultOf, readFragment } from "gql.tada";
// import { GetBlogPageBySlug } from "@queries/GetBlogPageBySlug";
import { MetadataHelmet } from "./MetadataHelmet";
// import { BackgroundBlob } from "./BackgroundBlob";
import { useScrollReset } from "@hooks/useScrollReset";
import { useTheme } from "@hooks/useTheme";
// import { TagFragment } from "@queries/fragments/TagFragment";
// import { ThemeFragment } from "@queries/fragments/ThemeFragment";
// import { defaultTheme } from "@utils/ThemeContext";
// import { isValidTheme } from "../../types/ThemeModel";
import { AccessibleMedia } from "@components/entries/AccessibleMedia";
import { BackgroundDeepContainer } from "./BackgroundDeepContainer";
import { RoutingSection } from "@components/entries/RoutingSection";
import { BlogBodySection } from "@components/entries/BlogComponents/BlogBodySection";
import { BlogCard } from "@components/entries/BlogComponents/BlogCard";
import { BlogTags } from "@components/entries/BlogComponents/BlogTags";
import { FootnoteProvider } from "@components/providers/FootnoteProvider";
import { FootnoteList } from "@components/shared/FootnoteList";
import { extractPageFootnotes } from "@utils/extractPageFootnotes";
import { getFormattedDate } from "@utils/textUtils";
import { useTranslation } from "@services/i18n";
import { OBELIX_BLOG_KEYS } from "@utils/obelixKeys";
import ReactMarkdown from 'react-markdown';

// type Page = NonNullable<
//   NonNullable<ResultOf<typeof GetBlogPageBySlug>["post"]>["items"][number]
// >;

// type Feed = NonNullable<
//   NonNullable<ResultOf<typeof GetBlogPageBySlug>["feed"]>["items"][number]
// >;

// type Tag = ResultOf<typeof TagFragment>;

export const BlogPage = ({
  page,
  blogFeed
}: {
  page: any;
  blogFeed: any;
}) => {
  useScrollReset();

  const { t } = useTranslation();
  const { setTheme } = useTheme();
  const { slug, postTitle, postSummary, heroImage, author, keyTakeaways}: any = page || false;

  // const theme = useMemo(
  //   () => readFragment(ThemeFragment, page?.theme as any) ?? defaultTheme,
  //   [page?.theme]
  // );

  useLayoutEffect(() => {
    // if (!isValidTheme(theme)) {
    //   console.warn(`Palette combination invalid: `, theme);
    //   return;
    // }

    // overriding until theme is added to contentful
    setTheme({
      background: 'neutral-light',
      foreground: 'neutral-dark',
      palette: 'cream'
    });
    // setTheme(theme);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // updatedDate takes precedence, then publicationDate and fallback to firstPublishedAt
  const dateToUse: string | null = page?.updatedDate || page?.publicationDate || page?.sys?.firstPublishedAt;
  const pubDate: string = getFormattedDate(dateToUse as string);
  const morePosts = blogFeed.filter((post: any) => page.slug !== post.slug);
  const tags = page?.tagsCollection?.items || false;
  const footnoteEntries = extractPageFootnotes([...(page?.bodyCollection?.items ?? [])]);

  return (
    <>
      <MetadataHelmet pageInfo={{ slug: slug, metadata: page?.metadata }} />
      <section id="blog-post" className="container-base max-w-[1440px] mx-auto my-[150px]">
        {pubDate &&
          <div className="byline font-medium text-center my-[60px]">
            <p>{pubDate} &ndash; by {author.name}</p>
          </div>
        }
   
        {postTitle &&
          <h1 className="text-heading-1 capitalize text-center max-w-[1280px] text-balance my-[64px] mx-auto">{postTitle}</h1>
        }

        {tags &&
          <BlogTags tags={tags} />
        }

        {postSummary &&
          <div className="postSummary my-[30px] max-w-[875px] mx-auto">
            <div className="text-center font-medium whitespace-pre-wrap">
              <ReactMarkdown>{postSummary}</ReactMarkdown>
            </div>
          </div>
        }

        {keyTakeaways &&
          <div className="my-[30px] max-w-[875px] mx-auto">
            <h2 className="text-heading-3 text-center my-[40px]">{t(OBELIX_BLOG_KEYS.keyTakeaways.obelixKey, OBELIX_BLOG_KEYS.keyTakeaways.label)}</h2>
            <div className="key-takeaways font-medium whitespace-pre-wrap">
              <ReactMarkdown>{keyTakeaways}</ReactMarkdown>
            </div>
          </div>
        }

        {heroImage &&
          <AccessibleMedia item={heroImage} />
        }

        {(page?.bodyCollection?.items?.length > 0) &&
          <FootnoteProvider footnoteEntries={footnoteEntries}>
            <div className="my-[30px] dsk:my-[60px]">
              <BlogBodySection items={page?.bodyCollection?.items} />
            </div>
            {(footnoteEntries.length > 0) &&
              <div className="sources max-w-[1014px] mx-auto">
                <h4 className="text-heading-4">Sources:</h4>
                <FootnoteList variant="dark" />
              </div>
            }
          </FootnoteProvider>
        }

        {author &&
          <div className="blog-author flex flex-col dsk:flex-row text-center dsk:text-left items-center dsk:items-start gap-[65px] max-w-[1014px] py-[30px] mx-auto">
            {author?.profilePhoto &&
              <div className="max-w-[256px]">
                <img
                  src={author.profilePhoto.url as string}
                  alt={author.profilePhoto?.title as string}
                  className="rounded-xl"
                />
              </div>
            }
            <div>
              {author.sectionTitle &&
                <h2 className="text-heading-3 mb-[36px]">{author.sectionTitle}</h2>
              }
              <p>{author.biography}</p>
            </div>
          </div>
        }

        {(morePosts?.length > 0) && (
          <div className="container-base my-[30px] dsk:my-[82px]">
            <h2 className="text-heading-1 capitalize text-center text-balance my-[30px] dsk:my-[64px]">
              {t(OBELIX_BLOG_KEYS.moreBlogPosts.obelixKey, OBELIX_BLOG_KEYS.moreBlogPosts.label)}
            </h2>
            <div className="grid dsk:grid-cols-3 gap-x-[50px] gap-y-[90px] justify-center">
              {morePosts.map((post: any) => (
                <BlogCard key={post.slug} post={post} />
              ))}
            </div>
          </div>
        )}
      </section>

      {page?.ctaFooter && (
        <BackgroundDeepContainer>
          <RoutingSection entry={{ routingSection: page.ctaFooter } as any} />
        </BackgroundDeepContainer>
      )}
    </>
  );
};
